/**
 * LanguageContext
 *
 * DE/EN UI-Toggle. Persistiert in localStorage. Markdown-Content bleibt
 * vorerst Deutsch (Anna's intime Texte sollen nicht maschinell übersetzt
 * im Frontend landen) — wenn EN aktiv ist, blendet der Content-Renderer
 * einen kleinen Hinweis ein.
 */

import { createContext, useContext, useEffect, useMemo, useState, useCallback, ReactNode } from 'react'

export type Language = 'de' | 'en'

interface LanguageContextValue {
  lang: Language
  setLang: (lang: Language) => void
  toggle: () => void
}

const LanguageContext = createContext<LanguageContextValue | null>(null)
const STORAGE_KEY = 'site-language'

function detectInitialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'de' || stored === 'en') return stored
  } catch {
    /* ignore */
  }
  const nav = (typeof navigator !== 'undefined' && navigator.language) || ''
  return nav.toLowerCase().startsWith('de') ? 'de' : 'en'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(detectInitialLanguage)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang)
    } catch {
      /* ignore */
    }
    document.documentElement.lang = lang
  }, [lang])

  const setLang = useCallback((next: Language) => setLangState(next), [])
  const toggle = useCallback(
    () => setLangState((prev) => (prev === 'de' ? 'en' : 'de')),
    [],
  )

  // Memoise — see LightingContext for the same anti-pattern + fix.
  const value = useMemo(
    () => ({ lang, setLang, toggle }),
    [lang, setLang, toggle],
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage requires <LanguageProvider>')
  return ctx
}

/**
 * Tiny string-table helper. Add keys here as the codebase grows.
 * EN translations are reviewed by hand — no auto-translate.
 */
export const STRINGS = {
  nav: {
    home: { de: 'Start', en: 'Home' },
    werke: { de: 'Werke', en: 'Works' },
    kuenstlerin: { de: 'Künstlerin', en: 'Artist' },
    ausstellungen: { de: 'Ausstellungen', en: 'Exhibitions' },
    kunstFuerDich: { de: 'Kunst für dich', en: 'Art for You' },
  },
  sections: {
    ausstellungenHeader: {
      de: 'Ausstellungen & Veröffentlichungen',
      en: 'Exhibitions & Publications',
    },
    artProjects: { de: 'Kunstprojekte', en: 'Art Projects' },
    currentExhibitions: { de: 'Aktuell', en: 'Currently on view' },
    statusRunning: { de: 'läuft', en: 'on view' },
    statusUpcoming: { de: 'kommt', en: 'upcoming' },
    statusPast: { de: 'vergangen', en: 'past' },
    moreInfo: { de: 'Mehr Infos →', en: 'More info →' },
    gallery: { de: 'Galerie', en: 'Gallery' },
    galleryTagline: {
      de: 'Entdecken Sie die Skulpturen-Serien von Anna Sacher Santana',
      en: 'Explore the sculpture series by Anna Sacher Santana',
    },
    gallerySeoDescription: {
      de: 'Alle Skulpturen-Serien von Anna Sacher Santana — von Yin/Yang/Yun über Dakinis und Yoginis bis Lichtskulpturen. Stöbern Sie durch 13 Kategorien.',
      en: 'All sculpture series by Anna Sacher Santana — from Yin/Yang/Yun through Dakinis and Yoginis to light sculptures. Browse 13 categories.',
    },
    viewWorks: { de: 'Werke ansehen', en: 'View works' },
    viewDetail: { de: 'Detail ansehen', en: 'View detail' },
  },
  /**
   * Curated EN names for the fixed set of artwork categories. Categories
   * come from folder names in the Drive CMS, so they don't get
   * auto-translated. Keys are the German folder name (without numeric
   * prefix). Add new ones here if Anna spins up a new series.
   */
  categories: {
    'Yin, Yang, Yun': { de: 'Yin, Yang, Yun', en: 'Yin, Yang, Yun' },
    'Feen, Götter und andere Zauberwesen': {
      de: 'Feen, Götter und andere Zauberwesen',
      en: 'Fairies, Gods and Other Enchanted Beings',
    },
    'Dakinis und Yoginis': { de: 'Dakinis und Yoginis', en: 'Dakinis and Yoginis' },
    'Energieformen': { de: 'Energieformen', en: 'Energy Forms' },
    'Paare': { de: 'Paare', en: 'Couples' },
    'Lichtskulpturen': { de: 'Lichtskulpturen', en: 'Light Sculptures' },
    'Kinder und Jugendpsychiatrische Skulpturen': {
      de: 'Kinder und Jugendpsychiatrische Skulpturen',
      en: 'Child & Adolescent Psychiatric Sculptures',
    },
    'Katalog, Skulpturen 2007–2009 – Therapeutische Visionen': {
      de: 'Katalog, Skulpturen 2007–2009 – Therapeutische Visionen',
      en: 'Catalogue, Sculptures 2007–2009 — Therapeutic Visions',
    },
    'Vom Inneren ins Sichtbare': {
      de: 'Vom Inneren ins Sichtbare',
      en: 'From the Inner to the Visible',
    },
    'Kalender 1999-2001': { de: 'Kalender 1999-2001', en: 'Calendar 1999–2001' },
    'Wie alles begann-Die Geburt einer Berufung': {
      de: 'Wie alles begann — Die Geburt einer Berufung',
      en: 'How It All Began — The Birth of a Calling',
    },
    'Out-door Impressionen': { de: 'Out-door Impressionen', en: 'Outdoor Impressions' },
    'Installationen für 5-Rhythmen-Workshops': {
      de: 'Installationen für 5-Rhythmen-Workshops',
      en: 'Installations for 5-Rhythms Workshops',
    },
  } as Record<string, { de: string; en: string }>,
  common: {
    loading: { de: 'Laden…', en: 'Loading…' },
    notFound: { de: 'Nicht gefunden', en: 'Not found' },
    back: { de: 'Zurück', en: 'Back' },
    backToGallery: { de: 'Zurück zur Galerie', en: 'Back to gallery' },
    backToArtist: { de: 'Zurück zu Künstlerin', en: 'Back to Artist' },
    backToExhibitions: { de: 'Zurück zu Ausstellungen', en: 'Back to Exhibitions' },
    readMore: { de: 'Weiterlesen →', en: 'Read more →' },
    image: { de: 'Bild', en: 'image' },
    images: { de: 'Bilder', en: 'images' },
    work: { de: 'Werk', en: 'work' },
    works: { de: 'Werke', en: 'works' },
    contact: { de: 'Kontakt', en: 'Contact' },
    media: { de: 'Medien', en: 'Media' },
    legal: { de: 'Rechtliches', en: 'Legal' },
    imprint: { de: 'Impressum', en: 'Imprint' },
    allRightsReserved: { de: 'Alle Rechte vorbehalten', en: 'All rights reserved' },
    contentGermanOnly: {
      de: '',
      en: 'Note: The artist\'s texts are currently available in German only — an English version is in preparation.',
    },
  },
  footer: {
    bio: {
      de: 'Bildhauerin · Kinder- und Jugendpsychiaterin',
      en: 'Sculptor · Child & Adolescent Psychiatrist',
    },
    studio: {
      de: 'Atelier am Wörthersee',
      en: 'Studio at Lake Wörthersee',
    },
    interviewsAndPress: { de: 'Interviews & Presse', en: 'Interviews & Press' },
  },
  langSwitch: {
    de: { de: 'DE', en: 'DE' },
    en: { de: 'EN', en: 'EN' },
    title: { de: 'Sprache wechseln', en: 'Switch language' },
  },
} as const

type StringNode = { de: string; en: string }
type StringTree = { [k: string]: StringNode | StringTree }

export function useT() {
  const { lang } = useLanguage()
  return function t(node: StringNode): string {
    return node[lang] ?? node.de
  }
}

/**
 * Pick a DE/EN field pair from the content index. Falls back to the DE
 * value when no EN translation exists yet — this way the site never
 * shows an empty page while the auto-translation pipeline catches up.
 *
 * Usage:
 *   const text = useFieldLang(artwork.description, artwork.description_en)
 */
export function useFieldLang() {
  const { lang } = useLanguage()
  return function pick<T extends string | null | undefined>(de: T, en?: T): T {
    if (lang === 'en' && en) return en
    return de
  }
}

/**
 * Translate a hard-coded category title (from a CMS folder name) via
 * the curated STRINGS.categories map. Falls back to the original when
 * the category isn't in the map yet.
 */
export function useCategoryTitle() {
  const { lang } = useLanguage()
  return function tCat(deTitle: string): string {
    const entry = STRINGS.categories[deTitle]
    if (entry) return entry[lang] || entry.de
    return deTitle
  }
}

// Type-export so callers can build their own per-page tables
export type { StringNode, StringTree }
